import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { Toolbar, AppBar, IconButton, Button, Tooltip } from '@material-ui/core';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@material-ui/core';
import ImportExportOutlinedIcon from '@material-ui/icons/ImportExportOutlined';
import PersonIcon from '@material-ui/icons/Person';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import StoreOutlinedIcon from '@material-ui/icons/StoreOutlined';
import { Link } from 'react-router-dom';

import { authLogin, setLatest } from '../redux/workBackendSlice';
import { isLogined, initializeSlices } from '../redux/dataBackend';
import { openShop } from '../redux/shopSlice';
import { updateUserCondition } from '../redux/userConditionSlice';
import useCurrentUserDb from '../hooks/useCurrentUserDb';
import useUserCondition from '../hooks/useUserCondition';

import { getTimestamp, clearTokensOfCookie } from '../general';
import DataConvertDialog from './DataConvertDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      color: "#000000",
      backgroundColor: "#ffffff"
    }
  })
);

const CustomAppBar = styled(AppBar)`
  height: 64px;
  border: solid 1px #e0e0e0;
`
const DownloadIconButton = styled(IconButton)`
  color: grey;
  margin-right: 8px;
`
const CustomPersonIcon = styled(PersonIcon)`
  color: grey;
  margin-right: 8px;
`
const BackIcon = styled(ArrowBackIcon)`
  color: grey;
  margin-right: 8px;
`
const UserName = styled(Box)`
  font-size: 18px;
  text-align: center;
  margin-right: 10px;
`
const PlainLink = styled(Link)`
  color: black;
  text-decoration: none;
`

export const hamburgerButtonId = 'hamburger-button';
const getName = (value?: string | null) => {
  if (!value || !isLogined()) return 'ログインしていません';
  else if (value.length >=7) return value.substring(0,5) + '...';
  else return value;
}

type DigimagiAppBarParent = 'UserHome' | 'Book' | 'UserSettings';

type UserButtonProps = {
  parent: DigimagiAppBarParent;
  appBarFrom?: '/' | '/book';
  openAccountMenu: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}
const UserButton: React.FC<UserButtonProps> = ({ parent, appBarFrom, openAccountMenu }) => {
  const [, currentUser] = useCurrentUserDb();
  const [name, setName] = useState(getName(currentUser?.name));

  useEffect(() => {
    if (!currentUser) return;
    else setName(getName(currentUser.name));
  }, [currentUser])

  const getLink = () => {
    const from = appBarFrom ? appBarFrom : '/';
    if (parent === 'UserSettings') return from;
    else return '/userSettings';
  }

  if (parent === 'UserSettings') return (
    <PlainLink to={getLink()}>
      <Button onClick={openAccountMenu}>
        <BackIcon />
        <UserName>{ name }</UserName>
      </Button>
    </PlainLink>
  );
  return (
    <Button onClick={openAccountMenu}>
      { isLogined() && <CustomPersonIcon /> }
      <UserName>{ name }</UserName>
      <UserName style={{ fontSize: 12, color: 'grey' }}>▼</UserName>
    </Button>
  );
}

type AccountMenuProps = {
  anchorEl: HTMLElement | null;
  closeAccountMenu: () => void;
  setRedirected: (value: boolean) => void;
  parent: DigimagiAppBarParent;
  appBarFrom?: '/' | '/book';
}
const AccountMenu: React.FC<AccountMenuProps> = ({ anchorEl, closeAccountMenu, setRedirected, parent, appBarFrom }) => {
  const dispatch = useDispatch();

  const getLink = () => {
    const from = appBarFrom ? appBarFrom : '/';
    if (parent === 'UserSettings') return from;
    else return '/userSettings';
  }
  const authSignOut = async () => {
    try {
      clearTokensOfCookie();
      dispatch(authLogin(false));
      dispatch(initializeSlices());
      dispatch(setLatest(false));
      setRedirected(false);
    } catch (err) {
      console.log(err);
    }
  }

  const loginedMenuItems = (
    <div>
      <PlainLink to={getLink()}>
        <MenuItem onClick={closeAccountMenu}>
          <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="ユーザ設定を表示／変更する" />
        </MenuItem>
      </PlainLink>
      <MenuItem onClick={authSignOut}>
        <ListItemIcon><PriorityHighIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="ログアウト" />
      </MenuItem>
    </div>
  );
  const noLoginedMenuItems = (
    <div>
      <PlainLink to="/signup">
        <MenuItem onClick={closeAccountMenu}>
          <ListItemIcon><PersonAddOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="ユーザ登録をする" />
        </MenuItem>
      </PlainLink>
      <PlainLink to="/userSettings">
        <MenuItem onClick={closeAccountMenu}>
          <ListItemIcon><PriorityHighIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="ログイン" />
        </MenuItem>
      </PlainLink>
    </div>
  );

  return (
    <Menu
      anchorEl={anchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={Boolean(anchorEl)}
      onClose={closeAccountMenu}
    >
      { isLogined() ? loginedMenuItems : noLoginedMenuItems }
    </Menu>
  );
}

type DigimagiAppBarProps = {
  setRedirected: (value: boolean) => void;
  parent: DigimagiAppBarParent;
  appBarFrom?: '/' | '/book';
  setAppBarFrom?: () => void;
}
const DigimagiAppBar: React.FC<DigimagiAppBarProps> = (props) => {
  const { setRedirected, parent, appBarFrom, setAppBarFrom } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [userId] = useCurrentUserDb();
  const [userConditionId] = useUserCondition(userId);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [exportAction, setExportAction] = useState<boolean>(false);

  const openHome = () => {
    if (userConditionId) dispatch(updateUserCondition({ id: userConditionId, where: 'folder', when: getTimestamp() }));
  }
  const openAccountMenu = (ev: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(ev.currentTarget);
  }
  const closeAccountMenu = () => {
    setAnchorEl(null);
  }

  useEffect(() => setAppBarFrom ? setAppBarFrom() : undefined, [])

  return (
    <>
      <CustomAppBar position="static" className={classes.root}>
        <Toolbar>
          <Tooltip title="フォルダを開く" placement="right">
            <PlainLink to="/" onClick={openHome}>
              <img src="/images/magicalogo.png"
                alt="digimagi"
                height="48"
                draggable="false"
                style={{ pointerEvents: 'none' }}
              />
            </PlainLink>
          </Tooltip>
          <Box style={{ flexGrow: 1 }}></Box>
          { parent !== 'UserSettings' &&
            <>
              <Button startIcon={<StoreOutlinedIcon />} onClick={() => dispatch(openShop())}>
                デジマジショップ
              </Button>
              <DownloadIconButton onClick={() => setExportAction(true)}>
                <ImportExportOutlinedIcon />
              </DownloadIconButton>
            </>
          }
          <UserButton parent={parent} appBarFrom={appBarFrom} openAccountMenu={openAccountMenu} />
        </Toolbar>
      </CustomAppBar>
      <AccountMenu anchorEl={anchorEl} closeAccountMenu={closeAccountMenu} setRedirected={setRedirected} parent={parent} appBarFrom={appBarFrom} />
      <DataConvertDialog open={exportAction} onClose={() => setExportAction(false)} />
    </>
  );
}

export default DigimagiAppBar;
